import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

import { orderAPI, configAPI } from '../services/api';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '../config/stripe';
import StripeElements from '../components/StripeElements';
import AuthModal from '../components/AuthModal';
import './Checkout.css';
import { useDocumentMeta } from '../seo/useDocumentMeta';

const Checkout = () => {
  useDocumentMeta();
  const { user, isAuthenticated, loading: authLoading, setAuthReturnPath, isGuestMode, guestEmail, setGuestEmailAddress, clearGuestMode } = useAuth();
  const { cartItems, getTotalPrice, removeOrderItems } = useCart();
  
  const navigate = useNavigate();
  const stripeElementsRef = useRef(null);
  
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [showOrderDetails, setShowOrderDetails] = useState(true);
  const [showFreeShippingDetails, setShowFreeShippingDetails] = useState(false);
  const [formData, setFormData] = useState({
    email: user?.email || '',
    shippingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      apartment: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
      phone: ''
    },
    billingAddress: {
      firstName: '',
      lastName: '',
      address: '',
      apartment: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
      phone: ''
    },
    useShippingAsBilling: true,
    paymentMethod: 'card',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
    billingZip: ''
  });
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState(null);
  // 兜底值必须是「保守」的：threshold 设 0 会让 subtotal >= threshold 恒成立，
  // 于是 /api/config 一挂就显示免运费，而服务端照收 SHIPPING_COST —— 少报 $10。
  // 用 Infinity 表示「未知就按要收运费算」，宁可显示得贵一点也不能少报。
  const [config, setConfig] = useState({
    freeShippingThreshold: Infinity,
    shippingCost: 10,
    taxRate: 0.1
  });

  // AuthContext 是在 effect 里从 localStorage 还原 user 的，
  // 而 formData 的初始值在 mount 时就固定了 —— 直接刷新/深链进 /checkout 时
  // user 还是 null，email 会一直空着，页面显示「Logged in as」且 CONTINUE 过不去。
  useEffect(() => {
    if (user?.email) {
      setFormData(prev => (prev.email ? prev : { ...prev, email: user.email }));
    }
  }, [user]);

  // 检查用户登录状态
  useEffect(() => {
    // 用 AuthContext 的 loading：本页那个 loading 只在提交订单时用，从来不为 true，
    // 拿它判断会在还没还原登录态时就闪一下登录框
    if (!isAuthenticated && !authLoading && !isGuestMode) {
      // 只设返回路径 + 打开本页自己的弹窗。
      // 不能再调 openAuthModal —— 那会把 Header 里的 AuthModal 也打开，
      // 两个 overlay 同为 z-index:1000，后渲染的盖住前一个，下层的按钮点不动。
      setAuthReturnPath('/cart');
      setShowAuthModal(true);
    }
  }, [isAuthenticated, authLoading, setAuthReturnPath, isGuestMode]);

  // 如果购物车为空，重定向到购物车页面（但不在订单成功跳转时触发）
  useEffect(() => {
    // 检查是否在订单成功页面，如果是则不重定向
    const currentPath = window.location.pathname;
    if (cartItems.length === 0 && !currentPath.includes('/order-success')) {
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  // 从后端获取配置
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const result = await configAPI.getConfig();
        if (result.success && result.data) {
          setConfig({
            freeShippingThreshold: result.data.freeShippingThreshold,
            shippingCost: result.data.shippingCost,
            taxRate: result.data.taxRate
          });
        }
      } catch (error) {
        console.error('Failed to fetch config:', error);
        // 使用默认值
      }
    };
    fetchConfig();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
      
      // 如果是 email 字段且是 guest mode，更新 guest email
      if (name === 'email' && isGuestMode) {
        setGuestEmailAddress(value);
      }
    }
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        // 对于 guest mode，必须验证 email 格式
        if (isGuestMode) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          return formData.email && emailRegex.test(formData.email);
        }
        return formData.email && formData.email.includes('@');
      case 2:
        const { firstName, lastName, address, city, state, postalCode, phone } = formData.shippingAddress;
        return firstName && lastName && address && city && state && postalCode && phone;
      case 3:
        return formData.paymentMethod && 
               (formData.paymentMethod !== 'card' || 
                (formData.cardNumber && formData.expiryDate && formData.cvv && formData.cardName));
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      setError(null);
    } else {
      setError('Please fill in all required fields');
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
    setError(null);
  };

  const calculatePricing = () => {
    const subtotal = getTotalPrice();
    const shipping = subtotal >= config.freeShippingThreshold ? 0 : config.shippingCost;
    const tax = Math.round(subtotal * config.taxRate * 100) / 100;
    const total = subtotal + shipping + tax;
    
    return { subtotal, shipping, tax, total };
  };

  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      // 创建订单
      const orderData = {
        email: formData.email,
        shippingAddress: formData.shippingAddress,
        billingAddress: formData.useShippingAsBilling ? formData.shippingAddress : formData.billingAddress,
        // 只传「买了什么」；单价、总价、卡信息都由后端从数据库和 Stripe 取，
        // 前端传的价格后端不会采信
        items: cartItems.map(item => ({
          product: item._id,
          quantity: item.quantity,
          size: item.size || null,
          sku: item.sku || null
        })),
        paymentMethod: 'card',
        useShippingAsBilling: formData.useShippingAsBilling,
        payment: {
          stripePaymentIntentId: paymentIntent.id
        }
      };

      const result = await orderAPI.createOrder(orderData);

      if (result.success) {
        // 保存订单号到localStorage（在跳转前保存）
        const orderNumber = result.data.orderNumber;
        localStorage.setItem('lastOrderNumber', orderNumber);
        
        // 先跳转到订单成功页面，避免触发购物车为空的检查
        navigate(`/order-success?orderNo=${orderNumber}`, { replace: true });
        
        // 跳转后只移除已购买的商品，保留购物车中的其他商品
        setTimeout(() => {
          // 只移除订单中包含的商品
          // 传购物车行的 id：同一商品的不同 size/sku 共享 _id，
          // 用 _id 会把没买的那一行也扣掉
          removeOrderItems(cartItems.map(item => ({
            id: item.id,
            quantity: item.quantity
          })));
          
          // 清除 guest mode
          if (isGuestMode) {
            clearGuestMode();
          }
        }, 100);
      } else {
        throw new Error(result.message || 'Failed to create order');
      }

    } catch (err) {
      setError('Failed to create order. Please contact support.');
      console.error('Order creation error:', err);
    }
  };

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    setLoading(false);
    
    // 如果是认证错误且不是 guest mode，显示登录提示
    if (errorMessage.includes('log in') && !isGuestMode) {
      setAuthReturnPath('/cart');
      setShowAuthModal(true);
    }
  };
  
  // 清除错误信息
  const clearError = () => {
    setError(null);
  };

  // 这个函数现在由StripeElements组件处理支付
  // 不再需要手动提交订单

  const pricing = calculatePricing();

  // 显示加载状态
  if (loading) {
    return (
      <div className="checkout-page">
        <div className="loading-container">
          <h2>Loading...</h2>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !authLoading && !isGuestMode) {
    return (
      <div className="checkout-page">
        <AuthModal 
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            navigate('/cart');
          }}
        />
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <h1 className="checkout-title">CHECKOUT</h1>
      <div className="checkout-container">
        <div className="checkout-content">
          {/* 左侧：结账步骤 */}
          <div className="checkout-left">
            {isGuestMode && (
              <div className="guest-notice">
                <strong>Guest Checkout</strong> - You are checking out as a guest. Your order will be associated with your email address.
              </div>
            )}
            
            <div className="checkout-steps">
            
            {/* Step 1: Email */}
            <div className={`checkout-step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-header">
                <span className="step-number">1</span>
                <span className="step-title">EMAIL ADDRESS</span>
              </div>
              {currentStep === 1 && (
                <div className="step-content">
                  {isGuestMode || isEditingEmail ? (
                    <div className="form-group">
                      <label htmlFor="email">Email Address*</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email Address*"
                        required
                      />
                      <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        *Required for order confirmation and tracking
                      </p>
                    </div>
                  ) : (
                    <div className="email-display-container">
                      <div className="email-display">
                        Logged in as {formData.email}
                      </div>
                      <button 
                        className="edit-btn"
                        onClick={() => setIsEditingEmail(true)}
                      >
                        EDIT
                      </button>
                    </div>
                  )}
                  
                  {/* Step 1 buttons */}
                  <div className="step-actions">
                    <button 
                      className="continue-btn"
                      onClick={handleNextStep}
                    >
                      CONTINUE
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Shipping */}
            <div className={`checkout-step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-header">
                <span className="step-number">2</span>
                <span className="step-title">SHIPPING</span>
              </div>
              {currentStep === 2 && (
                <div className="step-content">
                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name*</label>
                      <input
                        type="text"
                        name="shippingAddress.firstName"
                        value={formData.shippingAddress.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name*</label>
                      <input
                        type="text"
                        name="shippingAddress.lastName"
                        value={formData.shippingAddress.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Address*</label>
                    <input
                      type="text"
                      name="shippingAddress.address"
                      value={formData.shippingAddress.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Company, Apt, Suite, Unit (Optional)</label>
                    <input
                      type="text"
                      name="shippingAddress.apartment"
                      value={formData.shippingAddress.apartment}
                      onChange={handleInputChange}
                    />
                  </div>
                  
                  <div className="form-row form-row-three">
                    <div className="form-group">
                      <label>City*</label>
                      <input
                        type="text"
                        name="shippingAddress.city"
                        value={formData.shippingAddress.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>State*</label>
                      <input
                        type="text"
                        name="shippingAddress.state"
                        value={formData.shippingAddress.state}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Postal Code*</label>
                      <input
                        type="text"
                        name="shippingAddress.postalCode"
                        value={formData.shippingAddress.postalCode}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Phone Number*</label>
                    <input
                      type="tel"
                      name="shippingAddress.phone"
                      value={formData.shippingAddress.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="useShippingAsBilling"
                        checked={formData.useShippingAsBilling}
                        onChange={handleInputChange}
                      />
                      Use this address for billing too
                    </label>
                  </div>
                  
                  {/* Step 2 buttons */}
                  <div className="step-actions step-actions-no-border">
                    <button 
                      className="continue-btn"
                      onClick={handleNextStep}
                    >
                      CONTINUE
                    </button>
                    <button 
                      className="back-btn"
                      onClick={handlePrevStep}
                    >
                      BACK
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Step 3: Payment */}
            <div className={`checkout-step ${currentStep >= 3 ? 'active' : ''}`}>
              <div className="step-header">
                <span className="step-number">3</span>
                <span className="step-title">PAYMENT</span>
              </div>
              {currentStep === 3 && (
                <div className="step-content">
                  {/* Select Payment Method Section */}
                  <h4 className="payment-section-title">Select payment method</h4>
                  <div className="payment-methods">
                    <div className="payment-option selected">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={formData.paymentMethod === 'card'}
                        onChange={handleInputChange}
                      />
                      <div className="payment-option-content">
                        <span className="payment-title">Credit or Debit Card</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Add Card Section */}
                  <h4 className="payment-section-title">Add Card</h4>
                  <div className="stripe-payment-container">
                    <Elements stripe={stripePromise}>
                      <StripeElements
                        ref={stripeElementsRef}
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentError={handlePaymentError}
                        isGuestMode={isGuestMode}
                        showPayButton={false}
                        orderData={{
                          email: formData.email,
                          shippingAddress: formData.shippingAddress,
                          billingAddress: formData.useShippingAsBilling ? formData.shippingAddress : formData.billingAddress,
                          // size/sku 必须带上：后端要靠它们从 variants 里取真实单价，
                          // 少传就会按 base price 收款，和最终订单金额对不上
                          items: cartItems.map(item => ({
                            product: item._id,
                            quantity: item.quantity,
                            size: item.size || null,
                            sku: item.sku || null
                          })),
                          paymentMethod: 'card',
                          useShippingAsBilling: formData.useShippingAsBilling,
                          total: pricing.total
                        }}
                      />
                    </Elements>
                  </div>
                  
                  {/* Billing Address Checkbox */}
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="useShippingAsBilling"
                        checked={formData.useShippingAsBilling}
                        onChange={handleInputChange}
                      />
                      Billing address same as shipping
                    </label>
                  </div>
                  
                  {/* Billing Address Display/Input */}
                  {formData.useShippingAsBilling ? (
                    <div className="billing-address-display">
                      <h4 className="billing-address-title">Shipping Address</h4>
                      <div className="billing-address-content">
                        <p>{formData.shippingAddress.firstName} {formData.shippingAddress.lastName}</p>
                        <p>{formData.shippingAddress.address}{formData.shippingAddress.apartment ? `, ${formData.shippingAddress.apartment}` : ''}</p>
                        <p>{formData.shippingAddress.city} {formData.shippingAddress.state} {formData.shippingAddress.postalCode}</p>
                        <p>{formData.shippingAddress.country === 'US' ? 'United States' : formData.shippingAddress.country}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="billing-address-section">
                      <h4 className="billing-address-title">Billing Address</h4>
                      <div className="form-row">
                        <div className="form-group">
                          <label>First Name*</label>
                          <input
                            type="text"
                            name="billingAddress.firstName"
                            value={formData.billingAddress.firstName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Last Name*</label>
                          <input
                            type="text"
                            name="billingAddress.lastName"
                            value={formData.billingAddress.lastName}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Address*</label>
                        <input
                          type="text"
                          name="billingAddress.address"
                          value={formData.billingAddress.address}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Company, Apt, Suite, Unit (Optional)</label>
                        <input
                          type="text"
                          name="billingAddress.apartment"
                          value={formData.billingAddress.apartment}
                          onChange={handleInputChange}
                        />
                      </div>
                      
                      <div className="form-row form-row-three">
                        <div className="form-group">
                          <label>City*</label>
                          <input
                            type="text"
                            name="billingAddress.city"
                            value={formData.billingAddress.city}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>State*</label>
                          <input
                            type="text"
                            name="billingAddress.state"
                            value={formData.billingAddress.state}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Postal Code*</label>
                          <input
                            type="text"
                            name="billingAddress.postalCode"
                            value={formData.billingAddress.postalCode}
                            onChange={handleInputChange}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Phone Number*</label>
                        <input
                          type="tel"
                          name="billingAddress.phone"
                          value={formData.billingAddress.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Step 3 buttons */}
                  <div className="step-actions step-actions-payment">
                    <button 
                      className="pay-btn"
                      onClick={async () => {
                        // 用 state 而不是读 ref：ref 变化不会触发 re-render，
                        // 按钮既不会禁用也不会显示 Processing，用户能反复点。
                        if (paying || !stripeElementsRef.current) return;
                        setPaying(true);
                        try {
                          await stripeElementsRef.current.submit();
                        } finally {
                          setPaying(false);
                        }
                      }}
                      disabled={paying}
                    >
                      {paying ? 'Processing...' : `PAY $${pricing.total.toFixed(2)}`}
                    </button>
                    <button 
                      className="back-btn"
                      onClick={handlePrevStep}
                    >
                      BACK
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* 错误信息 */}
            {error && (
              <div className="error-message">
                <span>{error}</span>
                <button 
                  className="clear-error-btn" 
                  onClick={clearError}
                  style={{
                    marginLeft: '10px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    background: 'transparent',
                    border: '1px solid #000',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>
          </div>

          {/* 右侧：订单摘要 */}
          <div className="order-summary">
            <div className={`summary-header ${!showOrderDetails ? 'summary-header-collapsed' : ''}`}>
              <h3>ORDER SUMMARY</h3>
              <button 
                className="show-more-btn"
                onClick={() => setShowOrderDetails(!showOrderDetails)}
              >
                {showOrderDetails ? 'SHOW LESS ✓' : 'SHOW MORE ✓'}
              </button>
            </div>
            
            {showOrderDetails && (
            <div className="summary-items">
              {cartItems.map(item => (
                <div key={item.id} className="summary-item">
                  <img src={item.images && item.images.length > 0 ? item.images[0] : item.image || ''} alt={item.name} />
                  <div className="item-details">
                    <span className="item-name">{item.name}</span>
                    <span className="item-price">${item.price}</span>
                    <span className="item-quantity">Qty: {item.quantity}</span>
                  </div>
                </div>
              ))}
            </div>
            )}
            
            <div className="summary-totals">
              <div className="total-row">
                <span>Subtotal</span>
                <span>${pricing.subtotal.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Shipping</span>
                <span>
                  {pricing.shipping === 0 ? (
                    <div className="shipping-info">
                      <div>FREE</div>
                      {Number.isFinite(config.freeShippingThreshold) && config.freeShippingThreshold > 0 ? (
                        <div className="shipping-note">(Order reached ${config.freeShippingThreshold} free shipping threshold)</div>
                      ) : (
                        <div className="shipping-note">(Free shipping)</div>
                      )}
                    </div>
                  ) : (
                    <div className="shipping-info">
                      <div>${pricing.shipping.toFixed(2)}</div>
                      {Number.isFinite(config.freeShippingThreshold) && config.freeShippingThreshold > 0 && (
                        <div className="shipping-note">(Free shipping over ${config.freeShippingThreshold}, add ${(config.freeShippingThreshold - pricing.subtotal).toFixed(2)} more)</div>
                      )}
                    </div>
                  )}
                </span>
              </div>
              <div className="total-row">
                <span>Sales Tax</span>
                <span>${pricing.tax.toFixed(2)}</span>
              </div>
              <div className="total-row total-final">
                <span>Total</span>
                <span>${pricing.total.toFixed(2)}</span>
              </div>
            </div>
            
            <div className="tax-note">
              Taxes and duties included
            </div>
            
            <div className="summary-sections">
              <div className="collapsible-section">
                <button 
                  className="section-header"
                  onClick={() => setShowFreeShippingDetails(!showFreeShippingDetails)}
                >
                  <span>FREE SHIPPING & RETURNS</span>
                  <span className="toggle-icon">{showFreeShippingDetails ? '−' : '+'}</span>
                </button>
                {showFreeShippingDetails && (
                  <div className="section-content">
                    <p>P1ur provides free shipping.</p>
                    <p>Please allow up to 5-7 business days for your order to be processed and shipped. If you need to return a product, you must make your return request within 14 days from the recorded date of delivery.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;